import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FileText, Sun, Wifi } from "lucide-react";

import {
  CkycPublicData,
  ckycApi,
  needsCkycSessionRefresh,
  parseCkycPublicResponse,
  parseCkycSessionResponse,
} from "../../services/ckyc";
import type { HyperKycResult } from "../../types/hyperverge";
import { logApiErrorToSentry } from "../remittance-beta/utils";
import {
  PublicKycLanding,
  PublicKycLoading,
  PublicKycStatusLayout,
} from "../vkyc/PublicKycLayout";
import { vkycStyles as styles } from "../vkyc/styles";

type PageStatus =
  | "loading"
  | "ready"
  | "starting"
  | "expired"
  | "invalid"
  | "error"
  | "cancelled";

const VERIFICATION_STEPS = [
  {
    title: "Aadhaar Digilocker",
    mobileTitle: "Aadhaar",
    description: "Verify identity securely via Digilocker",
  },
  {
    title: "Live Photo",
    mobileTitle: "Live Photo",
    description: "Capture a live photo to complete CKYC",
  },
];

const KEEP_ITEMS = [
  {
    label: "Aadhaar number",
    hint: "Linked mobile number helps Digilocker",
    icon: FileText,
  },
  {
    label: "Quiet, well-lit space",
    hint: "Face the camera with even lighting",
    icon: Sun,
  },
  {
    label: "Stable internet",
    hint: "Do not switch networks mid-call",
    icon: Wifi,
  },
];

const EXPIRED_TIPS = [
  "Ask your agent/consultant to generate a new link",
  "Open it on the same device and browser",
  "Keep your Aadhaar ready before you start",
];

const getErrorMessage = (error: any) =>
  error?.response?.data?.error?.type ||
  error?.response?.data?.error ||
  error?.response?.error?.type ||
  error?.response?.error ||
  error?.message ||
  "Unable to open the CKYC link. Please contact support.";

const waitForHyperVergeSdk = async (timeoutMs = 10000) => {
  const startedAt = Date.now();
  while (!window.HyperKycConfig || !window.HyperKYCModule) {
    if (Date.now() - startedAt > timeoutMs) {
      throw new Error("HyperVerge SDK is not available");
    }
    await new Promise((resolve) => setTimeout(resolve, 50));
  }
};

const launchHyperKyc = async (
  jwtToken: string,
  workflowId: string,
  transactionId: string,
  handler: (result: HyperKycResult) => void
) => {
  await waitForHyperVergeSdk();
  const hyperKycConfig = new window.HyperKycConfig!(jwtToken, workflowId, transactionId, false);
  await window.HyperKYCModule!.launch(hyperKycConfig, handler);
};

const CkycLinkPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const [pageStatus, setPageStatus] = useState<PageStatus>("loading");
  const [statusMessage, setStatusMessage] = useState<string>();
  const [ckycData, setCkycData] = useState<CkycPublicData | null>(null);

  const loadCkycLink = useCallback(async () => {
    if (!token) {
      setPageStatus("invalid");
      setStatusMessage("This CKYC link is invalid. Please request a new link.");
      return;
    }

    setPageStatus("loading");

    try {
      const response = await ckycApi.getPublicCkyc(token);
      const data = parseCkycPublicResponse(response);

      if (!data) {
        setPageStatus("error");
        setStatusMessage("Unable to open the CKYC link. Please try again.");
        return;
      }

      if (data.mhLinkExpired || data.valid === false) {
        setPageStatus("expired");
        return;
      }

      if (data.completed) {
        window.location.replace("/kyc/confirmation");
        return;
      }

      setCkycData(data);
      setPageStatus("ready");
      if (data.appId && data.workflowId) {
        waitForHyperVergeSdk()
          .then(() => window.HyperKYCModule?.prefetch?.(data.appId!, data.workflowId!))
          .catch(() => undefined);
      }
    } catch (error: any) {
      logApiErrorToSentry(error, "ckycApi.getPublicCkyc");
      setPageStatus("error");
      setStatusMessage(getErrorMessage(error));
    }
  }, [token]);

  useEffect(() => {
    loadCkycLink();
  }, [loadCkycLink]);

  const resolveSession = async (current: CkycPublicData) => {
    let accessToken = String(current.hypervergeDlAccessToken || current.authToken || "").trim();
    let workflowId = current.workflowId || "";
    let ckycTransactionId = current.ckycTransactionId || "";

    if (needsCkycSessionRefresh(current)) {
      const mhAccessToken = current.mhAccessToken || token;
      if (!mhAccessToken) {
        throw new Error("Unable to refresh the CKYC session. Please request a new link.");
      }

      const sessionResponse = await ckycApi.createSession(mhAccessToken);
      const sessionData = parseCkycSessionResponse(sessionResponse);

      if (!sessionData) {
        throw new Error("Unable to start the CKYC session. Please try again.");
      }

      setCkycData((prev) => ({ ...prev, ...sessionData }));
      accessToken = String(
        sessionData.hypervergeDlAccessToken || sessionData.authToken || accessToken
      ).trim();
      workflowId = sessionData.workflowId || workflowId;
      ckycTransactionId = sessionData.ckycTransactionId || ckycTransactionId;
    }

    if (!accessToken || !workflowId || !ckycTransactionId) {
      throw new Error("CKYC session details are missing. Please try again.");
    }

    return { accessToken, workflowId, ckycTransactionId };
  };

  const handleHyperKycResult = (HyperKycResult: HyperKycResult) => {
    switch (HyperKycResult.status) {
      case "user_cancelled":
        setPageStatus("cancelled");
        setStatusMessage("Verification was cancelled. You can accept and proceed again.");
        break;
      case "error":
        setPageStatus("error");
        setStatusMessage(
          HyperKycResult.message || "CKYC verification could not be completed. Please try again."
        );
        break;
      case "auto_approved":
      case "auto_declined":
      case "needs_review":
        window.location.replace("/kyc/confirmation");
        break;
      default:
        setPageStatus("error");
        setStatusMessage(
          HyperKycResult.message || "CKYC verification could not be completed. Please try again."
        );
        break;
    }
  };

  const handleStartKyc = async () => {
    if (!ckycData) return;

    setPageStatus("starting");
    setStatusMessage(undefined);

    try {
      const { accessToken, workflowId, ckycTransactionId } = await resolveSession(ckycData);
      await launchHyperKyc(accessToken, workflowId, ckycTransactionId, handleHyperKycResult);
    } catch (error: any) {
      console.error("error ", error);
      logApiErrorToSentry(error, "ckycLaunchHyperKyc");
      setPageStatus("error");
      setStatusMessage(getErrorMessage(error));
    }
  };

  if (pageStatus === "loading" || pageStatus === "starting") {
    return (
      <PublicKycLoading
        message={
          pageStatus === "starting" ? "Opening verification..." : "Verifying your CKYC link..."
        }
      />
    );
  }

  if (pageStatus === "expired") {
    return (
      <PublicKycStatusLayout
        eyebrow="CKYC"
        contextTitle="This link is no longer active"
        contextBody="CKYC links expire within 24 hours to keep your details safe. Please request a new link and complete your KYC at the earliest."
        tips={EXPIRED_TIPS}
        tone="expired"
        statusTitle="Link expired"
        statusDescription="Your CKYC link has expired. Please request a new link from your agent or consultant to continue."
      />
    );
  }

  if (pageStatus === "invalid") {
    return (
      <PublicKycStatusLayout
        eyebrow="CKYC"
        contextTitle="This link is no longer active"
        contextBody="This CKYC link is invalid. Please request a new link from your agent or consultant."
        tips={EXPIRED_TIPS}
        tone="error"
        statusTitle="Invalid CKYC link"
        statusDescription={statusMessage}
      />
    );
  }

  if (pageStatus === "error") {
    return (
      <PublicKycStatusLayout
        eyebrow="CKYC"
        contextTitle="We could not open this KYC"
        contextBody="Something went wrong while opening your verification link. Please try again, or request a new link from your agent or consultant."
        tips={EXPIRED_TIPS}
        tone="error"
        statusTitle="Unable to start CKYC"
        statusDescription={statusMessage}
        action={
          <button type="button" onClick={loadCkycLink} className={styles.retryBtn}>
            Try again
          </button>
        }
      />
    );
  }

  return (
    <PublicKycLanding
      eyebrow="CKYC"
      title="Complete your verification"
      subtitleDesktop="Two steps, about 2-3 minutes. Keep Aadhaar ready before you start."
      subtitleCompact="Two steps, about 2-3 minutes. Keep Aadhaar ready."
      steps={VERIFICATION_STEPS}
      keepItems={KEEP_ITEMS}
      ctaLabel="Accept and Proceed"
      ctaBusy={false}
      ctaDisabled={!ckycData}
      onCta={handleStartKyc}
      notice={
        pageStatus === "cancelled" && statusMessage ? (
          <p className="text-center text-sm text-amber-700">{statusMessage}</p>
        ) : null
      }
    />
  );
};

export default CkycLinkPage;
